export const DEFAULT_FEEDBACK_QUESTION = 'Would you explore more from this creator?';

export const FEEDBACK_QUESTION_OPTIONS = [
  DEFAULT_FEEDBACK_QUESTION,
  'Would you listen to this artist again?',
  'Would you watch this?',
  'Would you buy this product?',
  'Would you follow this creator?',
  'Would you recommend this?',
  'Are you interested in learning more?'
];

export const DISCOVERY_INTERESTS = [
  { id: 'music_fans', label: 'Music Fans', completionTokenRate: 3 },
  { id: 'readers', label: 'Readers', completionTokenRate: 4 },
  { id: 'film_tv_fans', label: 'Film & TV Fans', completionTokenRate: 3 },
  { id: 'business_entrepreneurship', label: 'Business & Entrepreneurship', completionTokenRate: 5 },
  { id: 'fashion_beauty', label: 'Fashion & Beauty', completionTokenRate: 3 },
  { id: 'food', label: 'Food', completionTokenRate: 3 },
  { id: 'health_fitness', label: 'Health & Fitness', completionTokenRate: 4 },
  { id: 'technology', label: 'Technology', completionTokenRate: 5 },
  { id: 'gaming', label: 'Gaming', completionTokenRate: 3 },
  { id: 'sports', label: 'Sports', completionTokenRate: 3 },
  { id: 'education', label: 'Education', completionTokenRate: 4 },
  { id: 'travel', label: 'Travel', completionTokenRate: 4 }
];

const DISCOVERY_INTEREST_BY_ID = DISCOVERY_INTERESTS.reduce((map, interest) => {
  map[interest.id] = interest;
  return map;
}, {});

export function normalizeInterestIds(interestIds = []) {
  if (!Array.isArray(interestIds)) return [];
  return [...new Set(interestIds)].filter((interestId) => DISCOVERY_INTEREST_BY_ID[interestId]);
}

export function getInterestLabels(interestIds = []) {
  return normalizeInterestIds(interestIds).map((interestId) => DISCOVERY_INTEREST_BY_ID[interestId].label);
}

export function getCompletionTokenRate(interestIds = []) {
  const rates = normalizeInterestIds(interestIds).map((interestId) => DISCOVERY_INTEREST_BY_ID[interestId].completionTokenRate);
  return rates.length > 0 ? Math.max(...rates) : 0;
}

export function campaignMatchesViewerInterests(campaignInterestIds = [], viewerInterestIds = []) {
  const campaignIds = normalizeInterestIds(campaignInterestIds);
  const viewerIds = new Set(normalizeInterestIds(viewerInterestIds));
  return campaignIds.some((interestId) => viewerIds.has(interestId));
}

export const DESTINATION_PROVIDERS = [
  {
    id: 'spotify',
    label: 'Spotify',
    kind: 'music',
    domains: ['open.spotify.com'],
    supportsMediaPreview: true,
    getEmbedUrl: (url) => {
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) return `https://open.spotify.com/embed/${parts[0]}/${parts[1]}`;
      return null;
    }
  },
  {
    id: 'apple-music',
    label: 'Apple Music',
    kind: 'music',
    domains: ['music.apple.com'],
    supportsMediaPreview: true,
    getEmbedUrl: (url) => `https://embed.music.apple.com${url.pathname}${url.search}`
  },
  {
    id: 'tidal',
    label: 'TIDAL',
    kind: 'music',
    domains: ['tidal.com', 'listen.tidal.com'],
    supportsMediaPreview: true,
    getEmbedUrl: () => null
  },
  {
    id: 'pandora',
    label: 'Pandora',
    kind: 'music',
    domains: ['pandora.com', 'www.pandora.com'],
    supportsMediaPreview: true,
    getEmbedUrl: () => null
  },
  {
    id: 'youtube',
    label: 'YouTube',
    kind: 'video',
    domains: ['youtube.com', 'www.youtube.com', 'youtu.be'],
    supportsMediaPreview: true,
    getEmbedUrl: (url) => {
      const videoId = url.hostname === 'youtu.be'
        ? url.pathname.split('/').filter(Boolean)[0]
        : url.searchParams.get('v');
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
    }
  },
  {
    id: 'vimeo',
    label: 'Vimeo',
    kind: 'video',
    domains: ['vimeo.com', 'www.vimeo.com'],
    supportsMediaPreview: true,
    getEmbedUrl: (url) => {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
  },
  {
    id: 'bandcamp',
    label: 'Bandcamp',
    kind: 'music',
    domains: ['bandcamp.com'],
    supportsMediaPreview: true,
    getEmbedUrl: () => null
  },
  {
    id: 'even',
    label: 'EVEN',
    kind: 'music',
    domains: ['even.biz', 'www.even.biz'],
    supportsMediaPreview: true,
    getEmbedUrl: () => null
  },
  {
    id: 'x-twitter',
    label: 'X / Twitter',
    kind: 'social',
    domains: ['x.com', 'twitter.com', 'www.x.com', 'www.twitter.com'],
    supportsMediaPreview: false
  },
  {
    id: 'instagram',
    label: 'Instagram',
    kind: 'social',
    domains: ['instagram.com', 'www.instagram.com'],
    supportsMediaPreview: false
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    kind: 'social',
    domains: ['tiktok.com', 'www.tiktok.com'],
    supportsMediaPreview: false
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    kind: 'social',
    domains: ['linkedin.com', 'www.linkedin.com'],
    supportsMediaPreview: false
  },
  {
    id: 'facebook',
    label: 'Facebook',
    kind: 'social',
    domains: ['facebook.com', 'www.facebook.com'],
    supportsMediaPreview: false
  },
  {
    id: 'amazon',
    label: 'Amazon',
    kind: 'book',
    domains: ['amazon.com', 'www.amazon.com'],
    supportsMediaPreview: false
  },
  {
    id: 'barnes-noble',
    label: 'Barnes & Noble',
    kind: 'book',
    domains: ['barnesandnoble.com', 'www.barnesandnoble.com'],
    supportsMediaPreview: false
  },
  {
    id: 'website',
    label: 'Website',
    kind: 'website',
    domains: [],
    supportsMediaPreview: false,
    acceptsAnyHttps: true
  }
];

const MEDIA_PROVIDER_IDS = DESTINATION_PROVIDERS
  .filter((provider) => provider.supportsMediaPreview)
  .map((provider) => provider.id);

export function getDestinationProvider(platformId) {
  return DESTINATION_PROVIDERS.find((provider) => provider.id === platformId) || null;
}

export function getSuggestedDestinationProviders({ creatorType = '', discoveryPlatform = '' } = {}) {
  const priorityIds = [];
  if (creatorType === 'Music') priorityIds.push('spotify', 'apple-music', 'tidal', 'pandora', 'bandcamp');
  if (creatorType === 'Film and Video' || discoveryPlatform === 'YouTube') priorityIds.push('youtube', 'vimeo');
  if (creatorType === 'Books and Writing') priorityIds.push('amazon', 'barnes-noble');
  if (discoveryPlatform === 'Twitter') priorityIds.push('x-twitter');
  if (discoveryPlatform) priorityIds.push(discoveryPlatform.toLowerCase());

  const uniquePriorityIds = [...new Set(priorityIds)];
  const prioritized = uniquePriorityIds
    .map(getDestinationProvider)
    .filter(Boolean);
  const remaining = DESTINATION_PROVIDERS.filter(
    (provider) => !provider.acceptsAnyHttps && !uniquePriorityIds.includes(provider.id)
  );

  return [...prioritized, ...remaining];
}

export function parseHttpsUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return { status: 'empty' };

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:') return { status: 'invalid', message: 'Use a normal HTTPS link.' };
    return { status: 'valid', url };
  } catch {
    return { status: 'invalid', message: 'Use a normal HTTPS link.' };
  }
}

export function providerMatchesUrl(provider, url) {
  if (!provider || provider.acceptsAnyHttps) return Boolean(provider?.acceptsAnyHttps);
  return provider.domains.some((domain) =>
    url.hostname === domain || url.hostname.endsWith(`.${domain}`)
  );
}

export function getProviderForUrl(url, { mediaOnly = false } = {}) {
  return DESTINATION_PROVIDERS.find((provider) => {
    if (mediaOnly && !provider.supportsMediaPreview) return false;
    return providerMatchesUrl(provider, url);
  }) || null;
}

export function getMediaPreview(value) {
  const parsed = parseHttpsUrl(value);
  if (parsed.status === 'empty') return { status: 'empty' };
  if (parsed.status === 'invalid') return parsed;

  const provider = getProviderForUrl(parsed.url, { mediaOnly: true });
  if (!provider) return { status: 'invalid', message: 'This media provider is not recognized for the prototype.' };

  const embedUrl = provider.getEmbedUrl?.(parsed.url) || null;
  return {
    status: 'valid',
    provider: provider.label,
    providerId: provider.id,
    kind: provider.kind,
    embedUrl,
    href: parsed.url.toString(),
    message: embedUrl ? `${provider.label} can be previewed with manual playback.` : `${provider.label} will display as a trusted link preview.`
  };
}

export function getDestinationEmbed(destination) {
  if (!destination?.url) return null;
  const provider = getDestinationProvider(destination.platformId);
  if (!provider?.supportsMediaPreview || !provider.getEmbedUrl) return null;

  const parsed = parseHttpsUrl(destination.url);
  if (parsed.status !== 'valid' || !providerMatchesUrl(provider, parsed.url)) return null;
  return provider.getEmbedUrl(parsed.url);
}

export function getDestinationCtaLabel(destination) {
  const label = destination?.label || 'Destination';
  const kind = destination?.kind;

  if (kind === 'music') return `Listen on ${label}`;
  if (kind === 'video') return `Watch on ${label}`;
  if (kind === 'social') return `View on ${label}`;
  if (kind === 'book') return `View on ${label}`;
  if (kind === 'website') return 'Visit Website';
  return `Open ${label}`;
}

export function getLearnMorePreview(value) {
  const parsed = parseHttpsUrl(value);
  if (parsed.status === 'empty') return { status: 'empty' };
  if (parsed.status === 'invalid') return parsed;

  const trusted = DESTINATION_PROVIDERS.some((provider) =>
    !provider.acceptsAnyHttps && providerMatchesUrl(provider, parsed.url)
  );

  return {
    status: 'valid',
    href: parsed.url.toString(),
    clickable: trusted,
    message: trusted ? 'Trusted destination. The Learn More button can be clickable in this preview.' : 'Other official website - requires review before this link can be clickable.'
  };
}

export function getDestinationPreview(platformId, value) {
  const provider = getDestinationProvider(platformId);
  if (!provider) return { status: 'invalid', message: 'Choose a destination platform.' };

  const parsed = parseHttpsUrl(value);
  if (parsed.status === 'empty') return { status: 'empty' };
  if (parsed.status === 'invalid') return parsed;

  if (!providerMatchesUrl(provider, parsed.url)) {
    return {
      status: 'invalid',
      message: `Use a ${provider.label} HTTPS link for this destination.`
    };
  }

  return {
    status: 'valid',
    platformId: provider.id,
    label: provider.label,
    url: parsed.url.toString(),
    trusted: provider.kind !== 'website',
    kind: provider.kind,
    message: provider.kind === 'website'
      ? 'Website destination ready. It will open as an external link.'
      : `${provider.label} destination ready. It will open as an external link.`
  };
}

export function toCampaignDestination(destinationPreview) {
  if (destinationPreview?.status !== 'valid') return null;
  return {
    platformId: destinationPreview.platformId,
    label: destinationPreview.label,
    url: destinationPreview.url,
    trusted: destinationPreview.trusted,
    kind: destinationPreview.kind
  };
}

export function getFeedbackQuestion(value) {
  return FEEDBACK_QUESTION_OPTIONS.includes(value) ? value : DEFAULT_FEEDBACK_QUESTION;
}

export function getMediaProviderLabels() {
  return DESTINATION_PROVIDERS
    .filter((provider) => MEDIA_PROVIDER_IDS.includes(provider.id))
    .map((provider) => provider.label);
}
